import { Edit as EditIcon } from '@mui/icons-material';
import { Grid2 as Grid, Input, type Theme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useUpdateReport } from '@/admins/SmartReports/hooks/useUpdateReport';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import type { EventFor } from '@/types/helpers';
import { AssetReportVersion } from './AssetReportVersion';
import { BackToReportsButton } from './EditSmartReportActions';
import { useEditSmartReport } from './EditSmartReportContext';

const titleStyles = {
  fontSize: '2rem',
  fontWeight: 'bold',
  fontFamily: 'Barlow',
  baseline: 'middle',
};

const styles = {
  reportNameInput: {
    all: 'unset',
    height: 'auto',
    '&:hover': {
      cursor: 'text',
    },
    '& > input': {
      all: 'unset',
      height: 'auto',
    },
    ...titleStyles,
  },

  reportName: {
    ...titleStyles,
    color: (theme: Theme) => theme.palette.baseColors.deepSeaNavy.c75,

    '& > svg': {
      marginLeft: '0.5em',
      display: 'inline-block',
      opacity: 0,
      transition: 'opacity 0.3s',
    },

    '&:hover': {
      cursor: 'pointer',

      '& > svg': {
        opacity: 1,
      },
    },
  },
};

const SmartReportName = () => {
  const { report } = useEditSmartReport();
  const { setSnack } = useSnackBarContext();
  const [isEditing, setIsEditing] = useState(true);
  const [reportName, setReportName] = useState(report?.name || 'Untitled Report');

  const { updateReport, updatedReport, error, loading } = useUpdateReport();

  useEffect(() => {
    if (updatedReport?.id) {
      setSnack({
        message: 'Report name successfully updated.',
        severity: 'success',
        open: true,
      });
    }
  }, [updatedReport, setSnack]);

  useEffect(() => {
    if (error) {
      setSnack({
        message: 'Error updating report name. Please try again.',
        severity: 'error',
        open: true,
      });
    }
  }, [error, setSnack]);

  useEffect(() => {
    if (report) {
      setReportName(report.name);
    }
  }, [report]);

  if (isEditing) {
    const onNameBlur = () => {
      // setIsEditing(false);
      if (report) {
        setReportName(report?.name);
      }
    };
    const onSubmit = (e: EventFor<'input', 'onKeyDown'>) => {
      if (e.key === 'Enter') {
        setIsEditing(false);
        const editedName = reportName.trim() || 'Untitled Report';
        updateReport({ ...report, name: editedName });
      }
    };
    return (
      <Input
        sx={styles.reportNameInput}
        type='text'
        autoFocus
        value={reportName}
        disabled={loading}
        onBlur={onNameBlur}
        onKeyDown={onSubmit}
        onChange={e => setReportName(e.target.value)}
      />
    );
  }

  return (
    <Typography sx={styles.reportName} variant='h3' onClick={() => setIsEditing(true)}>
      {reportName} <EditIcon />
    </Typography>
  );
};

export const EditSmartReportHeader = () => {
  return (
    <>
      <Grid container justifyContent='space-between'>
        <BackToReportsButton />
      </Grid>
      <Grid
        display='grid'
        gridTemplateColumns={{
          lg: '1fr auto',
        }}
        sx={{ padding: 'var(--gap)' }}
      >
        <SmartReportName />
        <AssetReportVersion />
      </Grid>
    </>
  );
};
